import { CancelToken } from 'axios';
import axios from 'src/api/axiosInstances/privateAxiosInstance';
import { AlertResultsList, AlertResultExport } from '../types/AlertResults';
import { CategoriesExport } from 'src/components/ProductAlerts/types/AlertResults/CategoriesExport';

type AlertResultsFilterRequest = {
  alertId: string;
  sourceOwnerId: string;
  alertDate: string;
};

export type SourceOwnersItem = {
  id: string;
  name: string;
  isDefault: boolean;
};

export namespace SourceOwners {
  export type Request = Omit<AlertResultsFilterRequest, 'sourceOwnerId' | 'alertDate'>;

  export type Response = {
    sourceOwner: SourceOwnersItem[];
  };
}

type Region = {
  regionGroupName: string;
  region: {
    Id: number;
    value: string;
  }[];
};

export type CompetitorSitesItem = {
  ownerId: number;
  ownerName: string;
};

export namespace ClientRegions {
  export type Request = AlertResultsFilterRequest;

  export type Response = {
    Regions: Region[];
  };
}

export namespace CompetitorSites {
  export type Request = AlertResultsFilterRequest;

  export type Response = {
    competitorList: CompetitorSitesItem[];
  };
}

export namespace CompetitorFulfillers {
  export type Request = AlertResultsFilterRequest;

  export type Response = {
    fulfillerList: {
      fulfillers: string[];
    };
  };
}

export namespace CompetitorSellers {
  export type Request = AlertResultsFilterRequest;

  export type Response = {
    sellerList: {
      sellers: string[];
    };
  };
}

export namespace InsertionDates {
  export type Request = Omit<AlertResultsFilterRequest, 'alertDate'>;

  export type Response = {
    alertResultDateList: {
      alertResultDates: string[];
    };
  };
}

export type AlertItem = {
  alertId: number;
  alertName: string;
};

type AlertsResponse = {
  alerts: AlertItem[];
};

export const getAlertResults = (
  params: AlertResultsList.Request,
  cancelToken: CancelToken
): Promise<AlertResultsList.Response> => {
  return axios.get('qm/alertresult/retrieve', { params, cancelToken });
};

export const getSourceOwner = (params: SourceOwners.Request): Promise<SourceOwners.Response> => {
  return axios.get('/qm/sourceowner', { params });
};

export const getAlerts = (userEmail: string): Promise<AlertsResponse> => {
  return axios.get(`qm/alert/alertlist?email=${userEmail}`);
};

export const getInsertionDates = (params: InsertionDates.Request): Promise<InsertionDates.Response> => {
  return axios.get('qm/alert/resultdates', { params });
};

export const getClientRegions = async (params: ClientRegions.Request): Promise<ClientRegions.Response> => {
  return axios.get('qm/alert/regionfilter', { params });
};

export const getCompetitorSites = async (params: CompetitorSites.Request): Promise<CompetitorSites.Response> => {
  return axios.get('qm/alert/sourcecompetitor', { params });
};

export const getCompetitorSellers = (params: CompetitorSellers.Request): Promise<CompetitorSellers.Response> => {
  return axios.get('qm/alert/sellers', { params });
};

export const getCompetitorFulfillers = (
  params: CompetitorFulfillers.Request
): Promise<CompetitorFulfillers.Response> => {
  return axios.get('qm/alert/fulfillers', { params });
};

export const getAlertResultExportList = (
  params: AlertResultExport.Params,
  token: CancelToken
): Promise<AlertResultExport.Response> => {
  const config = {
    params,
    cancelToken: token,
    headers: {
      'Access-Control-Expose-Headers': 'Location' // Add this header to expose the Location header
    }
  };

  return axios.get('qm/alertresult/export?asynch=true', config);
};

export const getCategoryExportList = (
  params: CategoriesExport.Params,
  token: CancelToken
): Promise<CategoriesExport.Response> => {
  const config = {
    params,
    cancelToken: token,
    headers: {
      'Access-Control-Expose-Headers': 'Location' // Add this header to expose the Location header
    }
  };

  return axios.get('qm/category/export?asynch=true', config);
};
